import { SettingOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Flex,
  Modal,
  Switch,
  Tag,
  Typography,
  message
} from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useMemo, useState } from "react"

import { CustomTimePicker } from "../../../../../components/Date&TimePickers/CustomTimePicker.tsx"
import { TimezoneSelect } from "../../../../../components/Shared/TimezoneSelect.tsx"
import { useWorkingSchedule } from "../../../helpers/use-working-schedule.ts"
import {
  ALL_WEEKDAYS,
  areEnabledDaysUniform,
  DEFAULT_WORKING_SCHEDULE,
  type DaySchedule,
  validateWorkingSchedule,
  WEEKDAY_LABELS,
  type WeekdayIndex,
  type WorkingSchedule
} from "../../../helpers/working-schedule.ts"

const { Title, Text } = Typography

const parseTime = (time: string): Dayjs => {
  const [hour, minute] = time.split(":").map(Number)
  return dayjs().hour(hour).minute(minute).second(0).millisecond(0)
}

const formatTime = (value: Dayjs): string => value.format("HH:mm")

// Only Working Schedule is implemented. The rest are named here so the nav
// reads as a real settings surface with room to grow rather than a form with
// a decorative sidebar bolted on — each renders disabled with a "Soon" tag,
// never a working control with nothing behind it.
const SETTINGS_SECTIONS = [
  { key: "working-schedule", label: "Working Schedule", available: true },
  { key: "display", label: "Display", available: false },
  { key: "event-defaults", label: "Event Defaults", available: false },
  { key: "notifications", label: "Notifications", available: false }
] as const

interface CalendarSettingsDrawerProps {
  open: boolean
  accountTimezone?: string | null
  onClose: () => void
}

export function CalendarSettingsDrawer({
  open,
  accountTimezone,
  onClose
}: CalendarSettingsDrawerProps) {
  const [schedule, setSchedule] = useWorkingSchedule()
  const [draft, setDraft] = useState<WorkingSchedule>(schedule)
  const [sameHoursForAllDays, setSameHoursForAllDays] = useState(() =>
    areEnabledDaysUniform(schedule)
  )

  // Reopening always starts from the last saved value — an unsaved edit left
  // over from a previous open must never leak into the next one.
  useEffect(() => {
    if (!open) return
    setDraft(schedule)
    setSameHoursForAllDays(areEnabledDaysUniform(schedule))
    // Deliberately only on `open` — reacting to `schedule` too would overwrite
    // in-progress edits if a preference load resolves while the drawer is open.
  }, [open])

  const validation = useMemo(() => validateWorkingSchedule(draft), [draft])
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(schedule),
    [draft, schedule]
  )

  const updateDay = (day: WeekdayIndex, patch: Partial<DaySchedule>) => {
    setDraft((prev) => ({
      ...prev,
      days: { ...prev.days, [day]: { ...prev.days[day], ...patch } }
    }))
  }

  const applyHoursToAllDays = (start: string, end: string) => {
    setDraft((prev) => ({
      ...prev,
      days: ALL_WEEKDAYS.reduce(
        (acc, day) => {
          acc[day] = { ...prev.days[day], start, end }
          return acc
        },
        {} as WorkingSchedule["days"]
      )
    }))
  }

  const firstEnabledDay = ALL_WEEKDAYS.find((day) => draft.days[day].enabled)
  const uniformDay = draft.days[firstEnabledDay ?? 1]

  const handleSameHoursToggle = (checked: boolean) => {
    setSameHoursForAllDays(checked)
    if (!checked) return
    const source = firstEnabledDay ? draft.days[firstEnabledDay] : DEFAULT_WORKING_SCHEDULE.days[1]
    applyHoursToAllDays(source.start, source.end)
  }

  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)

  const handleClose = () => {
    if (!hasUnsavedChanges) {
      onClose()
      return
    }
    setConfirmDiscardOpen(true)
  }

  const handleConfirmDiscard = () => {
    setConfirmDiscardOpen(false)
    onClose()
  }

  const handleSave = () => {
    if (!validation.isValid) return
    setSchedule(draft)
    message.success("Working schedule updated")
    onClose()
  }

  const handleTimezoneChange = (value: string) => {
    setDraft((prev) => ({
      ...prev,
      // Picking the account's own timezone clears the override rather than
      // pinning that value — so a later account-timezone change (made from
      // Settings > Profile) is still followed live instead of going stale.
      timezone: value === accountTimezone ? null : value
    }))
  }

  const dayErrorMessage = useMemo(() => {
    const entries = Object.entries(validation.dayErrors) as [string, string][]
    if (entries.length === 0) return null
    if (sameHoursForAllDays) return "Start time must be before end time."
    return entries
      .map(([day, msg]) => `${WEEKDAY_LABELS[Number(day) as WeekdayIndex]}: ${msg}`)
      .join(" ")
  }, [validation.dayErrors, sameHoursForAllDays])

  return (
    <Drawer
      open={open}
      width={480}
      title={
        <Flex align="center" gap={8}>
          <SettingOutlined />
          <span>Calendar Settings</span>
        </Flex>
      }
      onClose={handleClose}
      destroyOnClose
      footer={
        <Flex justify="end" gap="small">
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" disabled={!validation.isValid} onClick={handleSave}>
            Save
          </Button>
        </Flex>
      }
    >
      <Flex gap={20} align="start">
        <nav
          aria-label="Calendar settings sections"
          className="flex w-[124px] shrink-0 flex-col gap-0.5"
        >
          {SETTINGS_SECTIONS.map((section) =>
            section.available ? (
              <div
                key={section.key}
                aria-current="true"
                className="rounded-md bg-primary/10 px-3 py-2 text-sm font-medium text-primary"
              >
                {section.label}
              </div>
            ) : (
              <button
                key={section.key}
                type="button"
                disabled
                className="flex cursor-not-allowed items-center justify-between rounded-md px-3 py-2 text-left text-sm text-gray-400"
              >
                {section.label}
                <Tag className="ml-1 text-[10px] leading-4">Soon</Tag>
              </button>
            )
          )}
        </nav>

        <Flex vertical gap={20} className="min-w-0 flex-1">
        <Flex vertical gap={4}>
          <Title level={5} className="mb-0">
            Working Schedule
          </Title>
          <Text type="secondary" className="text-sm">
            Set your working days, hours and time zone — Calendar uses this
            to show what&apos;s available.
          </Text>
        </Flex>

        <Flex vertical gap={8}>
          <Text strong>Work Week</Text>
          <Flex wrap gap={12}>
            {ALL_WEEKDAYS.map((day) => (
              <Checkbox
                key={day}
                checked={draft.days[day].enabled}
                onChange={(e) => updateDay(day, { enabled: e.target.checked })}
              >
                {WEEKDAY_LABELS[day]}
              </Checkbox>
            ))}
          </Flex>
        </Flex>

        <Flex vertical gap={8}>
          <Flex justify="space-between" align="center">
            <Text strong>Working Hours</Text>
            <Flex gap={8} align="center">
              <Text type="secondary" className="text-xs">
                Different hours per day
              </Text>
              <Switch
                size="small"
                aria-label="Use different working hours per day"
                checked={!sameHoursForAllDays}
                onChange={(checked) => handleSameHoursToggle(!checked)}
              />
            </Flex>
          </Flex>

          {sameHoursForAllDays ? (
            <Flex align="center" gap={8}>
              <CustomTimePicker
                aria-label="Start time"
                value={parseTime(uniformDay.start)}
                onChange={(value) =>
                  value && applyHoursToAllDays(formatTime(value), uniformDay.end)
                }
              />
              <Text type="secondary">—</Text>
              <CustomTimePicker
                aria-label="End time"
                value={parseTime(uniformDay.end)}
                onChange={(value) =>
                  value && applyHoursToAllDays(uniformDay.start, formatTime(value))
                }
              />
            </Flex>
          ) : (
            <Flex vertical gap={6}>
              {ALL_WEEKDAYS.map((day) => {
                const daySchedule = draft.days[day]
                return (
                  <Flex key={day} align="center" gap={8}>
                    <Text
                      className={`w-24 shrink-0 ${daySchedule.enabled ? "" : "text-gray-400"}`}
                    >
                      {WEEKDAY_LABELS[day]}
                    </Text>
                    <CustomTimePicker
                      aria-label={`${WEEKDAY_LABELS[day]} start time`}
                      disabled={!daySchedule.enabled}
                      value={parseTime(daySchedule.start)}
                      onChange={(value) =>
                        value && updateDay(day, { start: formatTime(value) })
                      }
                    />
                    <Text type="secondary">—</Text>
                    <CustomTimePicker
                      aria-label={`${WEEKDAY_LABELS[day]} end time`}
                      disabled={!daySchedule.enabled}
                      value={parseTime(daySchedule.end)}
                      onChange={(value) =>
                        value && updateDay(day, { end: formatTime(value) })
                      }
                    />
                  </Flex>
                )
              })}
            </Flex>
          )}
          {dayErrorMessage && <Alert type="error" showIcon message={dayErrorMessage} />}
        </Flex>

        <Flex vertical gap={8}>
          <Flex justify="space-between" align="center">
            <Text strong>Break</Text>
            <Switch
              size="small"
              aria-label="Enable break"
              checked={draft.breakTime.enabled}
              onChange={(checked) =>
                setDraft((prev) => ({
                  ...prev,
                  breakTime: { ...prev.breakTime, enabled: checked }
                }))
              }
            />
          </Flex>
          {draft.breakTime.enabled && (
            <Flex align="center" gap={8}>
              <CustomTimePicker
                aria-label="Break start time"
                value={parseTime(draft.breakTime.start)}
                onChange={(value) =>
                  value &&
                  setDraft((prev) => ({
                    ...prev,
                    breakTime: { ...prev.breakTime, start: formatTime(value) }
                  }))
                }
              />
              <Text type="secondary">—</Text>
              <CustomTimePicker
                aria-label="Break end time"
                value={parseTime(draft.breakTime.end)}
                onChange={(value) =>
                  value &&
                  setDraft((prev) => ({
                    ...prev,
                    breakTime: { ...prev.breakTime, end: formatTime(value) }
                  }))
                }
              />
            </Flex>
          )}
          {validation.breakError && (
            <Alert type="error" showIcon message={validation.breakError} />
          )}
        </Flex>

        <Flex vertical gap={8}>
          <Text strong>Time Zone</Text>
          <TimezoneSelect
            aria-label="Calendar time zone"
            value={draft.timezone ?? accountTimezone ?? undefined}
            onChange={handleTimezoneChange}
          />
        </Flex>
        </Flex>
      </Flex>

      <Modal
        open={confirmDiscardOpen}
        title="Discard unsaved changes?"
        okText="Discard"
        okButtonProps={{ danger: true }}
        cancelText="Keep editing"
        onOk={handleConfirmDiscard}
        onCancel={() => setConfirmDiscardOpen(false)}
      >
        Your working schedule changes have not been saved.
      </Modal>
    </Drawer>
  )
}
